/** lag-scroller
 *
 *  Provides width wise scrolling to contents, ie overflow-x:auto;
 *  but allows the scrollbar to stick to the bottom of the screen.
 *  
 *  EXAMPLE USAGE:
 *      <lag-scroller>
 *          Some content, like a...
 *          <table>...</table>
 *      </lag-scroller>
 */

// REFERENCES:
// https://stackoverflow.com/questions/23206877/sticky-scrollbar-at-bottom-of-table
// http://jsfiddle.net/TBnqw/2288/
// http://jsfiddle.net/8zoks7wz/1/

import { defineElement } from '../../utility/defineElement.js';
import { debounce } from '../../utility/debounce.js';

const SCROLLCONTAINER_ID = 'scrollContainer';
const OBSERVABLEBOTTOM_ID = 'observableBottom';
const STICKYSCROLLBAR_ID = 'stickyScrollbar';
const STICKYSCROLLBAR_VISIBLECLASS = 'visible';
const STICKYSCROLLBAR_TOPGAP = 60; // The px lag-scroller needs to scroll into view (at top) before showing stickyScrollbar
const STICKYSCROLLBAR_OBSERVERTHRESHOLDS = [...Array(101).keys()].map(k => k / 100); // 0.0, 0.1, 0.2, ..., 0.9, 1.0
const STICKYSCROLLBAR_DEBOUNCEPERIOD_MODIFY = 100; // ms
const STICKYSCROLLBAR_DEBOUNCEPERIOD_POSITION = 10; // ms

defineElement(class extends HTMLElement {

    static get is() { return 'lag-scroller'; }

    get _intersections() {
        if (!this._v_intersections) {
            this._v_intersections = {
                component: null,
                bottom: null
            };
        }

        return this._v_intersections;
    }

    get _contentWidth() {
        return this.$.scrollContainer.clientWidth;
    }
    get _contentScrollWidth() {
        return this.$.scrollContainer.scrollWidth;
    }
    get _contentScrollLeft() {
        return this.$.scrollContainer.scrollLeft;
    }
    set _contentScrollLeft(scrollLeft) {
        this.$.scrollContainer.scrollLeft = scrollLeft;
    }

    constructor() {
        super();

        const shadow = this.attachShadow({ mode: 'open' });

        // -- Styling --
        const style = document.createElement('style');
        style.textContent = `
:host { display: block; position: relative; }
#${SCROLLCONTAINER_ID} { position: relative; overflow-x: auto; }
#${OBSERVABLEBOTTOM_ID} { position: absolute; bottom: 0; width: 100%; height: 1px; }
#${STICKYSCROLLBAR_ID} { display: var(--lag-scroller-stickyScrollbar-display, block); visibility: hidden; position: fixed; overflow-x: scroll; bottom: 0; margin: 0 auto; }
#${STICKYSCROLLBAR_ID}.${STICKYSCROLLBAR_VISIBLECLASS} { visibility: visible; }
#${STICKYSCROLLBAR_ID} > div { height: 1px; }`;

        this.$ = {
            // -- Slot (Content) --
            slot: document.createElement('slot'),

            // -- Scrolling Container --
            // This has the actual overflow-x scrolling, we need this extra container rather than just
            // having the lag-scroller host provide the scrolling because we need the observableBottom
            // to be outside the scrolling content so it can remain fixed to its location (allowing for observation).
            scrollContainer: document.createElement('div'),

            // -- Observable Bottom --
            // Used to trigger IntersectionObserver when the bottom goes out of view (or into view).
            // Can't just use host component (lag-scroller) since the granularity of IntersectionObserver
            // leaves gaps which can cause it to not fire observe events when needed (ie fires 3px before
            // bottom of component goes out of view and then doesn't fire after it goes out of view, until top comes into view).
            observableBottom: document.createElement('div'),

            // -- StickyScrollbar --
            // Create div that will have a horizontal scrollbar that we can dynamically (via JS)
            // show/hide at the bottom of the viewport so as to provide a 'sticky' scrollbar.
            stickyScrollbar: document.createElement('div'),
            // Also need to add a div within the stickyScrollbar to cause an overflow scrollbar,
            // we will dynamically size this as per whatever the actual content is in our slot.
            stickyScrollbarContent: document.createElement('div')
        };

        this.$.scrollContainer.id = SCROLLCONTAINER_ID;

        this.$.observableBottom.id = OBSERVABLEBOTTOM_ID;

        this.$.stickyScrollbar.id = STICKYSCROLLBAR_ID;
        this.$.stickyScrollbar.appendChild(this.$.stickyScrollbarContent);

        shadow.appendChild(style);
        this.$.scrollContainer.appendChild(this.$.slot);
        shadow.appendChild(this.$.scrollContainer)
        shadow.appendChild(this.$.observableBottom);
        shadow.appendChild(this.$.stickyScrollbar);
    }

    connectedCallback() {
        this._unwireEvents(); // Element could be moved and connectedCallback invoked more than once so unwire here just-in-case.
        this._wireupEvents();

        // Initialize...
        this._syncScrollWidths();
    }

    disconnectedCallback() {
        this._unwireEvents();
    }

    // == Event Wireup/Unwire ==

    _wireupEvents() {
        // Intersection observer not supported, so we dont bother with stickyScrollbar (just use default scrollbar).
        if (!('IntersectionObserver' in window))
            return false;

        this._unwireables = []; // Initialize, will be added to by wireup calls, and used in _unwireEvents.

        // Sync the stickyScrollbar position on certain events.
        this._wireupEvents_syncScrollPosition();
        // Sync the stickyScrollbar scrolling width on certain events which could adjust the actual content scrolling width.
        this._wireupEvents_syncScrollWidths();
        // Show the stickyScrollbar appropriately as per its viewable position.
        this._wireupEvents_setStickyScrollbarVisibility();
    }
    _unwireEvents() {
        if (this._unwireables && this._unwireables.length)
            this._unwireables.forEach(uw => uw());
    }

    _wireupEvents_syncScrollPosition() {
        // When the stickyScrollbar is scrolled we make the lag-scroller (this) match the horizontal scrolling.
        this.$.stickyScrollbar.addEventListener('scroll', () => this._syncScrollPosition(this.$.stickyScrollbar.scrollLeft));
        // When the lag-scroller (this) is scrolled we make the stickyScrollbar match the horizontal scrolling.
        this.$.scrollContainer.addEventListener('scroll', () => this._syncScrollPosition(this._contentScrollLeft));

        // NOTE: Don't need to add unwireable entries for above events since they will be removed on DOM removal automatically.
    }

    _wireupEvents_syncScrollWidths() {
        const handler = () => this._syncScrollWidths(this.$.stickyScrollbar, this.$.stickyScrollbarContent);

        // On certain events we need to ensure the scroll width is sync'd up correctly.
        // Add to unwireables so they are removed from window event handlers when removed from dom/etc.
        window.addEventListener('load', handler);
        this._unwireables.push(_ => { window.removeEventListener('load', handler); });
        window.addEventListener('resize', handler);
        this._unwireables.push(_ => { window.removeEventListener('resize', handler); });

        // On content mutation we need to ensure the scroll width is sync'd up correctly.
        try {
            const MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
            new MutationObserver(handler).observe(this, { characterData: true, childList: true, subtree: true });
            // Don't need to add unwireable entries since this will be removed on DOM removal automatically.
        }
        catch (_ex) {
            // Some older browsers don't support MutationObserver,
            // instead we will use slotchange event, though this event only detects node removal/addition and
            // not content change (eg innerText/innerHTML).
            this.$.slot.addEventListener('slotchange', handler);
            // Don't need to add unwireable since this will be removed on DOM removal automatically.
        }
    }
    _wireupEvents_setStickyScrollbarVisibility() {
        // Observe observableBottom intersection which provides a definitive indication if the bottom of the
        // lag-scroller is viewable (since observableBottom is at the bottom). We use an independant element
        // of 1px in height to observe the visibility of the bottom rather than observing the lag-scroller
        // element itself since that would introduce issue due to the granular nature of IntersectionObserver
        // observations (see threshold). The primary issue is an observation can be fired just before the
        // bottom goes out of view (dont need stickyScrollbar yet) and then due to the threshold granularity
        // another observation wont be fired after the bottom goes out of view (when we want stickyScrollbar),
        // until the top of the element is visible and more observations will fire. Whereas using this specific
        // element (of 1px height) produces a clear 0 or 1 (hidden/visible) indication which we can use.
        new IntersectionObserver(
            entries => {
                this._intersections.bottom = entries[0];
                this._updateStickyScrollbarVisibilityDebounced();
            }) // No need for thresholds, default [0,1] is fine.
            .observe(this.$.observableBottom);

        // We also need to observe the lag-scroller component itself so we get updates when its intersection
        // changes but the observableBottom intersection doesn't change, eg if the bottom is already out of view
        // and we continue scrolling up and the whole lag-scroller goes out of view (below viewport) then the
        // observableBottom will never fire another intersection observation since it will not have changed,
        // but we still need to know that the lag-scroller has gone entirely out of view.
        new IntersectionObserver(
            entries => {
                this._intersections.component = entries[0];
                this._updateStickyScrollbarVisibilityDebounced();
            },
            { threshold: STICKYSCROLLBAR_OBSERVERTHRESHOLDS })
            .observe(this);
    }

    // == Logic & Helpers ==

    _updateStickyScrollbarVisibilityDebounced() {
        this._debounce_observeBottomIntersection = debounce(this._debounce_observeBottomIntersection, STICKYSCROLLBAR_DEBOUNCEPERIOD_MODIFY).then(() => {
            this._setStickyScrollbarVisibility(
                this._shouldStickyScrollbarBeVisible());
        });
    }

    _shouldStickyScrollbarBeVisible() {
        // If component intersection hasn't been set yet then wait until it is.
        if (!this._intersections.component)
            return false;

        // Should NOT show the scrollbar if the component IS NOT intersecting with viewport (ie not viewable).
        if (this._intersections.component.isIntersecting === false)
            return false;

        // NOTE: This check currently does not result in a correct result if a parent element is visibility:hidden,
        // ie in that case this function will still return true (assuming no other conditions change the result).
        const componentHidden =
            !(this.offsetWidth || this.offsetHeight /*|| this.getClientRects().length*/) // if display:none or not in DOM, etc
            || this.style.visibility === 'hidden' || this.style.visibility === 'collapse';
        // Even though the intersection indicates intersection with viewport the component is otherwise hidden.
        if (componentHidden)
            return false;

        // Should NOT show the scrollbar if the bottom IS visible, since native scrollbar would be visible.
        // NOTE that bottom intersection may not be set, if it isn't then (ignore) assume it is out of view,
        // ie isIntersecting is effectively false (not currently viewable, therefore scrollbar is eligible).
        if (this._intersections.bottom && this._intersections.bottom.isIntersecting === true)
            return false;

        const componentBoundingRect = this._intersections.component.boundingClientRect;
        // If only a little bit of the top of the component (topgap) is showing then don't show the scrollbar
        // as the scrollbar would just overlap the content and wouldn't be useful anyways.
        if ((componentBoundingRect.top + STICKYSCROLLBAR_TOPGAP) > document.documentElement.clientHeight)
            return false;

        // Everything has checked out, we should show the stickyScrollbar.
        return true;
    }

    _setStickyScrollbarVisibility(visible) {
        this.$.stickyScrollbar.classList.toggle(STICKYSCROLLBAR_VISIBLECLASS, visible);
    }

    _syncScrollPosition(scrollLeft) {
        this._debounce_syncScrollPosition = debounce(this._debounce_syncScrollPosition, STICKYSCROLLBAR_DEBOUNCEPERIOD_POSITION).then(() => {
            this._contentScrollLeft = this.$.stickyScrollbar.scrollLeft = scrollLeft;
        });
    }

    _syncScrollWidths() {
        this._debounce_syncScrollWidths = debounce(this._debounce_syncScrollWidths, STICKYSCROLLBAR_DEBOUNCEPERIOD_MODIFY).then(() => {
            if (!this._shouldStickyScrollbarBeVisible())
                return;

            // Sync the width of the stickyScrollbar's content to the actual content's scrolling (content) width.
            this.$.stickyScrollbarContent.style.width = this._contentScrollWidth + 'px';
            // Sync the width of the stickyScrollbar itself to the actual lag-scroller width.
            this.$.stickyScrollbar.style.width = this._contentWidth + 'px';
            // Sync the scroll position between stickyScrollbar and the actual content scroll.
            this.$.stickyScrollbar.scrollLeft = this._contentScrollLeft;
        });
    }

});